// ─────────────────────────────────────────────────────────────────
//  BuildPro · VerticalDescriptor + main component
//  File: modules/buildpro/BuildPro.tsx
//
//  Integration in VerticalEngine:
//    import { buildProDescriptor } from './modules/buildpro/BuildPro';
//    <VerticalEngine verticals={[buildProDescriptor]} />
// ─────────────────────────────────────────────────────────────────
'use client';

import React, { useState, useRef, useCallback } from 'react';
import {
  Hammer, Building2, Trees, Upload, ScanLine, Ruler,
  Droplets, Zap, Flame, PlugZap, Lightbulb, ToggleLeft, Cuboid,
  CheckCircle2, AlertTriangle, ChevronDown, ChevronUp,
  Download, Save, RotateCcw, Home, Eye, EyeOff, X,
  MapPin, Loader2, Settings, Key,
} from 'lucide-react';

import type {
  BuildProProps, BuildProMode, BuildProItem, BuildProImage,
  BuildProMarkers, BuildProScanResult, BuildProParams,
  BuildProQuote, VerticalDescriptor, QuoteItem,
} from './types';
import { getCatalogue, getAllItemsMap, AI_KEY_MAP } from './catalogue';
import type { CatalogueCategory } from './catalogue';
import { QuotePDFRenderer } from './QuotePDFRenderer';
import { CadGenerator } from './CadGenerator';
import * as pdfjsLib from 'pdfjs-dist';

// Configure pdf.js worker locally for Vite/Tauri offline stability
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

// ── Constants ────────────────────────────────────────────────────
const VAT = 0.21;

const MODE_CONFIG = {
  reforma:  { label: 'Reforma',              icon: Hammer,    color: 'blue',   accent: '#3B82F6', promptType: 'reforma'  },
  nueva:    { label: 'Obra nueva',            icon: Building2, color: 'green',  accent: '#22C55E', promptType: 'nueva'    },
  exterior: { label: 'Exterior / Urbanización', icon: Trees,  color: 'yellow', accent: '#EAB308', promptType: 'exterior' },
} as const;

const HVAC_OPTS = [
  { id: 'caldera_rad',  label: 'Caldera + radiadores' },
  { id: 'caldera_sue',  label: 'Caldera + suelo radiante' },
  { id: 'aerotermia',   label: 'Aerotermia' },
  { id: 'splits',       label: 'Splits / multisplit' },
  { id: 'conductos',    label: 'Conductos centralizados' },
  { id: 'mixto',        label: 'Sistema mixto' },
];

const TIPO_OBRA = [
  'Vivienda unifamiliar', 'Piso/apartamento', 'Local comercial',
  'Nave industrial', 'Almacén/depósito', 'Oficinas/despachos',
  'Casa de invitados', 'Garaje independiente', 'Otro',
];

const TIPO_EXTERIOR = [
  'Cerramiento parcela', 'Acceso y garaje', 'Piscina y spa',
  'Ajardinamiento', 'Iluminación exterior', 'Drenaje y acometidas',
  'Obra completa exterior', 'Otro',
];

// ── API Key Management ────────────────────────────────────────────
const STORAGE_KEY_GEMINI = 'BUILDPRO_GEMINI_KEY';

export const getGeminiKey = (): string | null => localStorage.getItem(STORAGE_KEY_GEMINI);
export const setGeminiKey = (key: string) => {
  if (!key) localStorage.removeItem(STORAGE_KEY_GEMINI);
  else localStorage.setItem(STORAGE_KEY_GEMINI, key);
};

// Groq key management + AI features
import { getGroqKey, setGroqKey, explainBudgetItem, suggestAlternatives, analyzeBudgetSavings } from '@/services/groqService';
// DDC Skills — Takeoff, Cost Calculator, Value Engineering
import { calculateProjectSummary, calculateProjectWaste, identifyVETargets } from '@/services/ddcSkillsService';

// ── Helpers ───────────────────────────────────────────────────────
const fv = (v: unknown): number => parseFloat(String(v)) || 0;
const eur = (v: number) =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);

async function readImage(file: File): Promise<BuildProImage> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const result = r.result as string;
      resolve({ b64: result.split(',')[1], mime: result.split(';')[0].replace('data:', ''), url: result });
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

async function readPDFasImage(file: File): Promise<BuildProImage> {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const page = await pdf.getPage(1);
  const scale = 1.5; // Optimized for performance/quality ratio
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const ctx = canvas.getContext('2d')!;
  
  // Enforce white background for JPEG compression
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  await page.render({ canvasContext: ctx, viewport }).promise;
  const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
  return {
    b64: dataUrl.split(',')[1],
    mime: 'image/jpeg',
    url: dataUrl,
  };
}

/** Render ALL pages of a PDF to images */
async function readPDFAllPages(file: File): Promise<BuildProImage[]> {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const pages: BuildProImage[] = [];
  
  // Dynamic scale to prevent Out Of Memory on massive architecture plans
  const scale = Math.min(1.5, 3.0 / Math.sqrt(pdf.numPages));
  
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext('2d')!;
    
    // Enforce white background for JPEG
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    await page.render({ canvasContext: ctx, viewport }).promise;
    const dataUrl = canvas.toDataURL('image/jpeg', 0.82);
    pages.push({ b64: dataUrl.split(',')[1], mime: 'image/jpeg', url: dataUrl });
  }
  return pages;
}

/** Call Google Gemini API with images and text */
async function callGemini(parts: unknown[]): Promise<string> {
  const apiKey = getGeminiKey();
  if (!apiKey) throw new Error('No se ha configurado la API key de Google. Ve a ⚙️ Configuración en la pantalla principal para introducirla.');
  const res = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-05-20:generateContent?key=${apiKey}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      contents: [{ parts }],
      generationConfig: { maxOutputTokens: 16384, temperature: 0.2 },
    }),
  });
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    const errMsg = errData?.error?.message || `Error ${res.status}`;
    if (res.status === 400 && errMsg.includes('API key')) throw new Error('API key de Google inválida. Revísala en ⚙️ Configuración.');
    throw new Error(`Gemini API: ${errMsg}`);
  }
  const data = await res.json();
  const content = data.candidates?.[0]?.content?.parts?.[0]?.text;
  if (!content) throw new Error('Gemini no devolvió contenido. Intenta de nuevo.');
  return content;
}

/** Parse AI response lines into a quantity map keyed by item id */
function parseAIResponse(raw: string): Record<string, number> {
  const qty: Record<string, number> = {};
  for (const line of raw.split('\n')) {
    const m = line.match(/^([A-Z][A-Z0-9B]+):([\d.]+)/);
    if (m) {
      const itemId = AI_KEY_MAP[m[1]];
      if (itemId) qty[itemId] = fv(m[2]);
    }
  }
  return qty;
}

/** Build QuoteItem-compatible array from qty map, enriching with dynamic prices if available */
function buildQuoteItems(mode: BuildProMode, qty: Record<string, number>, customPrices: Record<string, { price: number; source: 'ddc' | 'local'; breakdown?: any }> = {}): BuildProItem[] {
  const itemsMap = getAllItemsMap();
  const result: BuildProItem[] = [];
  for (const cat of getCatalogue(mode)) {
    for (const item of cat.items) {
      const q = qty[item.id] ?? 0;
      if (q <= 0) continue;
      const meta = itemsMap.get(item.id);
      const custom = customPrices[item.id];
      const finalPrice = custom?.price ?? item.p;
      
      result.push({
        // QuoteItem fields
        id:       item.id,
        name:     item.label,
        quantity: q,
        unit:     item.ud,
        price:    finalPrice,
        total:    finalPrice * q,
        // BuildProItem extensions
        cod:          item.cod,
        category:     meta?.categoryLabel ?? cat.label,
        categoryId:   cat.id,
        mode,
        source:       (custom?.source === 'ddc' ? 'api' : custom?.source) ?? 'local',
        breakdown:    custom?.breakdown,
      });
    }
  }
  return result;
}

/** Build the AI prompt for each mode */
function buildPrompt(mode: BuildProMode, params: BuildProParams, markers: BuildProMarkers, dual: boolean): string {
  const scaleNote = markers.scale
    ? `SCALE CALIBRATED: 1cm=${markers.scale.pxPerCm.toFixed(3)}px. Use this to calculate all areas precisely.`
    : 'No scale calibration. Use realistic Spanish construction estimates.';

  let mkNote = '';
  if (markers.agua)               mkNote += 'Water supply marked on plan.\n';
  if (markers.luz)                mkNote += 'Electrical panel/acometida marked.\n';
  if (markers.gas)                mkNote += 'Gas supply marked.\n';
  if (markers.enchufe?.length)    mkNote += `COUNTED: ${markers.enchufe.length} power sockets → use exact count.\n`;
  if (markers.luminaria?.length)  mkNote += `COUNTED: ${markers.luminaria.length} light points → use exact count.\n`;
  if (markers.interruptor?.length) mkNote += `COUNTED: ${markers.interruptor.length} switches → use exact count.\n`;

  const planNote = dual ? 'TWO PLANS provided (before + after / site + project). Compare and extract quantities.' : 'SINGLE PLAN provided.';

  const baseInstruction = `Expert Spanish quantity surveyor.
${planNote}
${scaleNote}
${mkNote}
Output ONLY structured lines, no markdown, no explanations.`;

  if (mode === 'reforma') return `${baseInstruction}
Mode: REFORMA. ceiling=${params.alturaLibre}m tiles_h=${params.alturaAlicatado}m hvac=${params.hvac} doors=${params.puertas} windows=${params.ventanas}
${params.notas}

RESUMEN: <text>
SUPERFICIE: <m2>
CONFIANZA: alta|media|baja
CAMBIO: <text>
ROOM: id|name|tipo|area_m2|perim_ml|estado
D1:<ml> D2:<ud> D3:<ud> D4:<m2> D5:<m2> D6:<ud> D7:<m2> D8:<ud> D11:<m3>
T1:<m2> T2:<m2> T3:<m2> T4:<m2> T5:<m2> T6:<ml> T7:<m2> T9:<ud>
C1:<ud> C2:<ud> C3:<ud> C4:<ud> C5:<ud> C6:<ud> C7:<ml> C9:<ud> C10:<ud> C11:<ud>
K1:<ml> K2:<ml> K3:<ml> K3B:<ml> K4:<ud> K5:<ud> K5B:<ud> K6:<ud> K7:<ud> K8:<ud> K9:<ud> K10:<ud> K11:<ud> K12:<ud> K13:<ud>
B1:<ud> B2:<ud> B3:<ud> B3B:<ud> B3C:<ud> B4:<ud> B5:<ud> B6:<ud> B6B:<ud> B7:<ud> B8:<ud> B9:<ud> B10:<ud> B10B:<ud> B11:<ud> B12:<ud> B13:<ud> B14:<ud> B16:<ud>
H1:<ud> H1B:<ud> H2:<ud> H4:<elem> H5:<elem> H6:<m2> H7:<m2> H8:<ud> H8B:<ud> H8C:<ud> H9:<ud> H9B:<ud> H11:<ud> H12:<ud> H14:<ud>
F1:<ml> F2:<ml> F3:<ml> F4:<ud> F5:<ud> F6:<ud> F7:<ud> F9:<ud> F10:<ud> F11:<ud> F12:<ml>
E1:<ud> E2:<ud> E3:<ud> E4:<ud> E5:<ud> E6:<ud> E7:<ud> E7B:<ud> E8:<ud> E8B:<ud> E8C:<ud> E9:<ud> E10:<ud> E10B:<ud> E10C:<ud> E10D:<ud> E11:<ud> E12:<ud> E12B:<ud> E12C:<ud> E12D:<ud> E13:<ud> E14:<ud> E15:<ud> E16:<ml> E18:<ud>
A1:<m2> A1B:<m2> A1C:<m2> A1D:<m2> A1E:<m2> A2:<ml> A3:<m2> A4:<m2> A4B:<m2> A5:<m2> A5B:<m2> A5C:<m2> A5D:<m2> A6:<m2> A7:<m2> A7B:<m2> A8:<m2> A9:<m2> A10:<ml>`;

  if (mode === 'nueva') return `${baseInstruction}
Mode: OBRA NUEVA. tipo=${params.tipoObra} ceiling=${params.alturaLibre}m hvac=${params.hvac}
${params.notas}

INSTRUCCIONES CRÍTICAS OBLIGATORIAS:
1. Actúa como un Arquitecto Técnico Senior (Aparejador) en España desarrollando un presupuesto de Ejecución Material (PEM) real y exhaustivo para un proyecto de OBRA NUEVA a partir de planos.
2. TU MAYOR PRIORIDAD ES LA EXHAUSTIVIDAD. No te dejes ninguna partida necesaria para que el edificio funcione. Mide las áreas superficiales de la planta y las elevaciones volumétricas de fachada y cubierta.
3. SIEMPRE INICIA CON UN BLOQUE <thinking>...</thinking> ANTES DE LA SALIDA ESTRUCTURADA.
   Dentro de <thinking>, debes calcular matemáticamente:
   - A) Superficie en planta (Huella) y total construida. Perímetro aproximado.
   - B) Movimiento de Tierras: Volumen de excavación (Huella x profundidad).
   - C) Cimentación y Estructura: ¿Hay sótano? Si sí -> Losa (CI4) y Muro (CI7). Si no -> Zapatas (CI2) o Losa. Cuantifica m3 de hormigón (aprox 0.35m3 por m2 de techo).
   - D) Cerramientos y Cubierta: Área de fachada exterior = Perímetro x Altura x Plantas. Deduce % huecos.
   - E) Instalaciones (Vital): Cuenta baños y cocinas. Asigna red general (IN1, IN2, IN3), cuadro eléctrico (IN7), domótica si procede. Distribuye IN8 por m2. Calcula puntos de luz y enchufes según m2 y habitaciones.

4. TRAS EL BLOQUE THINKING, ESCRIBE RIGUROSAMENTE:

MEMORIA: <Genera aquí una breve pero rigurosa memoria descriptiva arquitectónica y constructiva en Markdown del sistema adoptado, qué incluye la estructura, fachada e instalaciones. Máximo 2 párrafos.>

RESUMEN: <text>
SUPERFICIE_UTIL: <m2>
SUPERFICIE_CONSTRUIDA: <m2>
PLANTAS: <num>
CONFIANZA: alta|media|baja
ROOM: id|name|tipo|area_m2|planta
MV1:<m2> MV2:<m3> MV3:<m3> MV4:<m3> MV5:<m3> MV6:<m3> MV7:<m2>
CI1:<m3> CI2:<m3> CI3:<m3> CI4:<m3> CI5:<m3> CI7:<m2> CI8:<m3> CI9:<m3> CI10:<m2> CI11:<m2> CI12:<m2> CI13:<kg> CI14:<ud>
CE1:<m2> CE2:<m2> CE3:<m2> CE4:<m2> CE5:<m2> CE6:<m2> CE7:<m2> CE8:<m2> CE9:<m2> CE10:<m2> CE11:<m2> CE12:<ud> CE13:<m2> CE14:<m2> CE15:<m2> CE16:<m2>
CP1:<ud> CP2:<ud> CP3:<ud> CP4:<ud> CP5:<ud> CP6:<ud> CP7:<ud> CP8:<ml> CP9:<ud> CP10:<ud> CP11:<m2>
IN1:<ud> IN2:<ud> IN3:<ud> IN4:<ud> IN5:<ud> IN6:<ud> IN7:<ud> IN8:<m2> IN9:<ud> IN10:<ud> IN11:<ud> IN12:<m2> IN13:<ud> IN14:<ud> IN15:<kWp> IN16:<ud> IN17:<ud> IN18:<ud> IN19:<ud> IN20:<ud>
BN1:<ud> BN2:<ud> BN3:<ml> BN4:<ml> BN5:<ud> BN6:<ud> BN7:<ud> BN8:<ud> BN9:<ud>
AC1:<m2> AC2:<m2> AC3:<m2> AC4:<m2> AC5:<m2> AC6:<m2> AC7:<m2> AC8:<ml> AC9:<m2> AC11:<m2>`;

  // exterior
  return `${baseInstruction}
Mode: EXTERIOR/URBANIZACIÓN. focus=${params.tipoExterior}
${params.notas}

RESUMEN: <text>
SUPERFICIE_PARCELA: <m2>
CONFIANZA: alta|media|baja
CAMBIO: <text>
XM1:<m2> XM2:<m3> XM3:<m3> XM4:<m2> XM5:<m3>
V1:<m2> V2:<m2> V3:<m2> V4:<ml> V5:<ml> V6:<m2> V7:<m2> V8:<m2> V9:<ml> V10:<ml> V11:<m2> V12:<ml> V13:<ml>
P1:<ud> P2:<ud> P3:<ud> P4:<ud> P5:<ud> P6:<ud> P7:<ud> P8:<ud> P9:<ud> P10:<ud> P11:<ud>
PV1:<m2> PV2:<m2> PV3:<m2> PV4:<m2> PV5:<m2> PV6:<m2> PV7:<m2> PV8:<ml> PV9:<m2> PV10:<m2>
PS1:<ud> PS2:<m2> PS3:<ud> PS4:<m2> PS5:<m2> PS6:<ud> PS7:<ud> PS8:<ud> PS9:<ud> PS10:<ud> PS11:<ud> PS12:<ud> PS13:<m2> PS14:<ud>
J1:<m2> J2:<m2> J3:<m2> J4:<m2> J5:<ud> J6:<ud> J7:<m2> J8:<m2> J9:<ud> J10:<ml> J11:<m2> J12:<m2> J13:<ud> J14:<ml> J15:<ud> J16:<ml> J17:<ud> J18:<ml> J19:<ud> J20:<ud> J21:<ud>
BQ1:<ud> BQ2:<ud> BQ3:<ud> BQ4:<ud> BQ5:<ud> BQ6:<ml> BQ7:<ud> BQ8:<ml> BQ9:<m2> BQ10:<ud> BQ11:<ud> BQ12:<ud> BQ13:<ud> BQ14:<m2> BQ15:<m2> BQ16:<m2>
IL1:<ud> IL2:<ud> IL3:<ud> IL4:<ml> IL5:<ud> IL6:<ml> IL7:<ud> IL8:<ud> IL9:<ud>
DR1:<ud> DR2:<ud> DR3:<ud> DR4:<ud> DR5:<ml> DR6:<ml> DR7:<ud> DR8:<ud> DR9:<ud> DR10:<ud> DR11:<ml> DR12:<ml>`;
}

// ── Sub-components ────────────────────────────────────────────────

interface ModeCardProps { mode: BuildProMode; onClick: () => void; }
const ModeCard: React.FC<ModeCardProps> = ({ mode, onClick }) => {
  const cfg = MODE_CONFIG[mode];
  const Icon = cfg.icon;
  const colorMap = { blue: 'border-blue-500/30 hover:border-blue-500 hover:bg-blue-500/5', green: 'border-green-500/30 hover:border-green-500 hover:bg-green-500/5', yellow: 'border-yellow-500/30 hover:border-yellow-500 hover:bg-yellow-500/5' };
  const iconMap = { blue: 'bg-blue-500/20 text-blue-400', green: 'bg-green-500/20 text-green-400', yellow: 'bg-yellow-500/20 text-yellow-400' };
  const badgeMap = { blue: 'bg-blue-500/10 text-blue-400', green: 'bg-green-500/10 text-green-400', yellow: 'bg-yellow-500/10 text-yellow-400' };
  return (
    <button onClick={onClick} className={`group relative flex flex-col items-center gap-4 p-7 rounded-2xl border-2 bg-white/5 backdrop-blur-sm transition-all duration-200 text-left ${colorMap[cfg.color]}`}>
      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${iconMap[cfg.color]}`}>
        <Icon size={28} />
      </div>
      <div>
        <div className="text-base font-bold text-white mb-1">{cfg.label}</div>
        <div className="text-xs text-white/40 leading-relaxed">
          {mode === 'reforma'  && 'Rehabilitación y reforma de viviendas, pisos y locales existentes'}
          {mode === 'nueva'    && 'Construcción desde cero: vivienda, nave, local, oficina, almacén…'}
          {mode === 'exterior' && 'Vallas, piscinas, jardines, garajes, barbacoas, acometidas…'}
        </div>
      </div>
      <span className={`text-xs font-semibold px-3 py-1 rounded-full ${badgeMap[cfg.color]}`}>Empezar →</span>
    </button>
  );
};

interface DropZoneProps { label: string; sub: string; img: BuildProImage | null; onChange: (img: BuildProImage | null) => Promise<void>; }
const DropZone: React.FC<DropZoneProps> = ({ label, sub, img, onChange }) => {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (f: File) => {
    const isPDF = f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf');
    const isImage = f.type.startsWith('image/');
    if (!isPDF && !isImage) return;
    await onChange({ b64: '', mime: '', url: '', loading: true } as BuildProImage);
    try {
      const i = isPDF ? await readPDFasImage(f) : await readImage(f);
      await onChange(i);
    } catch (err) {
      console.error('Error reading file:', err);
      await onChange(null);
    }
  };

  return (
    <div className="relative">
      <div onClick={() => inputRef.current?.click()} onDragOver={e => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={async e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files?.[0]; if (f) await handleFile(f); }}
        className={`flex flex-col items-center justify-center gap-3 min-h-36 p-4 rounded-xl border-2 border-dashed cursor-pointer transition-all duration-150 ${dragging ? 'border-white/40 bg-white/10' : img?.url ? 'border-white/20 bg-white/5' : 'border-white/10 bg-white/3 hover:border-white/20 hover:bg-white/5'}`}>
        <input ref={inputRef} type="file" accept="image/*,.pdf,application/pdf" className="hidden" onChange={async e => { const f = e.target.files?.[0]; if (f) await handleFile(f); }} />
        {img?.loading ? (
          <><Loader2 size={24} className="animate-spin text-white/40" /><span className="text-xs text-white/40">Cargando…</span></>
        ) : img?.url ? (
          <><img src={img.url} alt="" className="max-h-28 max-w-full rounded-lg object-contain" /><span className="text-xs text-white/40">Clic para cambiar</span></>
        ) : (
          <><Upload size={24} className="text-white/30" /><div className="text-sm font-medium text-white/50">{label}</div><div className="text-xs text-white/25 text-center">{sub}</div></>
        )}
      </div>
      {img?.url && <button onClick={e => { e.stopPropagation(); onChange(null); }} className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/60 border border-white/20 text-white/60 hover:text-white flex items-center justify-center"><X size={12} /></button>}
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────
const BuildProComponent: React.FC<BuildProProps> = ({ dbAdapter, projectId, onQuoteSaved }) => {
  type Step = 'welcome' | 'upload' | 'scanning' | 'quantities' | 'summary';

  const [step, setStep] = useState<Step>('welcome');
  const [mode, setMode] = useState<BuildProMode>('reforma');
  const [imgA, setImgA] = useState<BuildProImage | null>(null);
  const [imgR, setImgR] = useState<BuildProImage | null>(null);
  const [params, setParams] = useState<BuildProParams>({ alturaLibre: 2.5, alturaAlicatado: 2.4, hvac: 'caldera_rad', notas: '', puertas: true, ventanas: false, tipoObra: 'Vivienda unifamiliar', tipoExterior: 'Cerramiento parcela' });
  const [markers, setMarkers] = useState<BuildProMarkers>({});
  const [qty, setQty] = useState<Record<string, number>>({});
  const [customPrices, setCustomPrices] = useState<Record<string, { price: number; source: 'ddc' | 'local'; breakdown?: any }>>({});
  const [openCats, setOpenCats] = useState<Record<string, boolean>>({});
  const [scanResult, setScanResult] = useState<BuildProScanResult | null>(null);
  const [showPrices, setShowPrices] = useState(false);
  const [showCad, setShowCad] = useState(false);
  const [savedQuoteId, setSavedQuoteId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const hasApiKey = !!getGeminiKey();
  const hasGroqKey = !!getGroqKey();
  const [aiPanel, setAiPanel] = useState<{ type: 'explain' | 'alternatives' | 'savings'; content: string; loading: boolean; itemName?: string } | null>(null);
  const [projectFiles, setProjectFiles] = useState<BuildProImage[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const multiInputRef = useRef<HTMLInputElement>(null);

  const cfg = MODE_CONFIG[mode];
  const cats: CatalogueCategory[] = getCatalogue(mode);
  const totalExVat = cats.reduce((s, c) => s + c.items.reduce((cs, it) => {
    const p = customPrices[it.id]?.price ?? it.p;
    return cs + p * (qty[it.id] ?? 0);
  }, 0), 0);

  const startMode = (m: BuildProMode) => { setMode(m); setStep('upload'); setQty({}); setProjectFiles([]); setOpenCats(Object.fromEntries(getCatalogue(m).map(c => [c.id, true]))); };

  const goHome = () => { setStep('welcome'); setScanResult(null); setImgA(null); setImgR(null); setProjectFiles([]); setQty({}); setError(null); setMarkers({}); };

  const handleMultiFiles = async (files: FileList) => {
    setLoadingFiles(true);
    try {
      const newImages: BuildProImage[] = [];
      for (const f of Array.from(files)) {
        const isPDF = f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf');
        const isImage = f.type.startsWith('image/');
        if (!isPDF && !isImage) continue;
        if (isPDF) {
          const pages = await readPDFAllPages(f);
          newImages.push(...pages);
        } else {
          const img = await readImage(f);
          newImages.push(img);
        }
      }
      setProjectFiles(prev => [...prev, ...newImages]);
    } catch (err) {
      console.error('Error reading files:', err);
      setError('Error al leer algunos archivos. Inténtalo de nuevo.');
    } finally {
      setLoadingFiles(false);
    }
  };

  const scan = useCallback(async () => {
    // For "nueva" mode, use projectFiles; for others, use imgA/imgR
    if (mode === 'nueva') {
      if (projectFiles.length === 0) return;
    } else {
      const img = imgR ?? imgA;
      if (!img?.url) return;
    }
    setError(null);
    setStep('scanning');
    try {
      let geminiParts: unknown[];
      let dual = false;

      if (mode === 'nueva') {
        // Send all project files as Gemini parts
        geminiParts = [];
        projectFiles.forEach((pf, i) => {
          geminiParts.push({ text: `PLANO ${i + 1} de ${projectFiles.length}:` });
          geminiParts.push({ inline_data: { mime_type: pf.mime, data: pf.b64 } });
        });
      } else {
        dual = !!imgA && !!imgR;
        const img = imgR ?? imgA;
        geminiParts = dual
          ? [{ text: 'ANTES:' }, { inline_data: { mime_type: imgA!.mime, data: imgA!.b64 } }, { text: 'DESPUÉS:' }, { inline_data: { mime_type: imgR!.mime, data: imgR!.b64 } }]
          : [{ text: 'PLANO:' }, { inline_data: { mime_type: img!.mime, data: img!.b64 } }];
      }

      const prompt = buildPrompt(mode, params, markers, dual);
      const rawFull = await callGemini([...geminiParts, { text: prompt }]);
      
      // Strip <thinking> blocks from response before parsing
      const raw = rawFull.replace(/<thinking>[\s\S]*?<\/thinking>/gi, '').trim();
      
      let memoria = '';
      const memMatch = raw.match(/MEMORIA:\s*([\s\S]*?)(?=RESUMEN:|$)/);
      if (memMatch) {
         memoria = memMatch[1].trim();
      }

      const lines = raw.split('\n').map(l => l.trim()).filter(Boolean);
      const get = (k: string) => { const l = lines.find(x => x.startsWith(k + ':')); return l ? l.slice(k.length + 1).trim() : ''; };
      const getAll = (k: string) => lines.filter(l => l.startsWith(k + ':')).map(l => l.slice(k.length + 1).trim());

      const parsedQty = parseAIResponse(raw);
      setQty(parsedQty);

      const rooms = getAll('ROOM').map(r => {
        const p = r.split('|');
        return { id: p[0] ?? '', nombre: p[1] ?? p[0] ?? '', tipo: p[2] ?? 'otro', area_m2: fv(p[3]), perimetro_ml: fv(p[4]), estado: p[5]?.trim(), planta: p[5]?.trim() };
      });

      const baseItems = buildQuoteItems(mode, parsedQty);
      
      // Enrich AI-detected items with live market prices from BuildCalculator.io
      const { batchEnrichPrices } = await import('@/services/buildCalculatorService');
      const apiResults = await batchEnrichPrices(baseItems.map(i => ({ label: i.name, fallbackPrice: i.price })));
      
      const newPrices = { ...customPrices };
      apiResults.forEach(r => {
        const item = baseItems.find(i => i.name === r.label);
        if (item && r.source === 'ddc') {
          newPrices[item.id] = { price: r.price, source: 'ddc', breakdown: r.breakdown };
        }
      });
      setCustomPrices(newPrices);

      const items = buildQuoteItems(mode, parsedQty, newPrices);

      setScanResult({
        resumen: get('RESUMEN'),
        memoria,
        superficie: fv(get('SUPERFICIE') || get('SUPERFICIE_UTIL') || get('SUPERFICIE_PARCELA')),
        superficieCons: fv(get('SUPERFICIE_CONSTRUIDA')),
        plantas: get('PLANTAS'),
        confianza: (get('CONFIANZA') as 'alta' | 'media' | 'baja') ?? 'media',
        cambios: getAll('CAMBIO'),
        rooms,
        items,
        rawLines: lines,
        scannedAt: new Date().toISOString(),
        mode,
        tipoObra: params.tipoObra,
      });

      // Sync catalogue items to DB
      await dbAdapter.upsertItems(items);

      setStep('quantities');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setStep('upload');
    }
  }, [imgA, imgR, projectFiles, mode, params, markers, dbAdapter]);

  const saveQuote = async () => {
    if (!scanResult) return;
    setSaving(true);
    try {
      const quoteItems: QuoteItem[] = buildQuoteItems(mode, qty, customPrices).map(({ id, name, quantity, unit, price, total }) => ({ id, name, quantity, unit, price, total }));
      const quote: BuildProQuote = {
        projectId,
        mode,
        tipoObra: params.tipoObra,
        resumen: scanResult.resumen,
        memoria: scanResult.memoria,
        superficie: scanResult.superficie,
        confianza: scanResult.confianza,
        items: quoteItems,
        totalExVat,
        vat21: totalExVat * VAT,
        totalIncVat: totalExVat * (1 + VAT),
        createdAt: new Date().toISOString(),
      };
      const id = await dbAdapter.saveQuote(quote);
      setSavedQuoteId(id);
      onQuoteSaved?.(id);
      setStep('summary');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────

  if (step === 'welcome') return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 flex items-center justify-center p-6">
      <div className="max-w-3xl w-full">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 mb-4 shadow-lg shadow-blue-500/25"><ScanLine size={32} className="text-white" /></div>
          <h1 className="text-3xl font-bold text-white mb-2">BuildPro</h1>
          <p className="text-white/40 text-sm">Escaneo de planos con IA · 345 partidas · Presupuesto instantáneo</p>
        </div>
        <div className="grid grid-cols-3 gap-4 mb-6">
          {(['reforma', 'nueva', 'exterior'] as BuildProMode[]).map(m => <ModeCard key={m} mode={m} onClick={() => startMode(m)} />)}
        </div>

        {/* API Key Configuration Link */}
        <div className="mt-6 max-w-xl mx-auto">
          <button onClick={() => window.location.hash = '#/configuracion-obra'} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all ${hasApiKey ? 'border-green-500/30 bg-green-500/5 hover:bg-green-500/10' : 'border-orange-500/30 bg-orange-500/5 hover:bg-orange-500/10'}`}>
            <div className="flex items-center gap-2">
              <Settings size={16} className={hasApiKey ? 'text-green-400' : 'text-orange-400'} />
              <span className={`text-sm font-medium ${hasApiKey ? 'text-green-400' : 'text-orange-400'}`}>
                {hasApiKey && hasGroqKey ? '✅ APIs de IA configuradas — Ver Configuración' : '⚠️ Configura tus API Keys de IA para empezar'}
              </span>
            </div>
            <span className="text-xs font-bold px-2 py-1 rounded-md bg-white/10 text-white/50">Abrir ajustes</span>
          </button>
        </div>
      </div>
    </div>
  );

  if (step === 'scanning') return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 flex items-center justify-center p-6">
      <div className="text-center">
        <Loader2 size={48} className="animate-spin text-blue-400 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-white mb-2">Analizando con IA…</h2>
        <p className="text-white/40 text-sm">Extrayendo mediciones del plano</p>
      </div>
    </div>
  );

  if (step === 'upload') return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={goHome} className="text-white/40 hover:text-white/80 transition-colors"><Home size={18} /></button>
          <span className="text-white/20">/</span>
          <cfg.icon size={18} style={{ color: cfg.accent }} />
          <span className="font-semibold text-white">{cfg.label}</span>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm flex items-start gap-2">
            <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-auto"><X size={14} /></button>
          </div>
        )}

        {/* Dropzones — multi-file for obra nueva, dual for reforma/exterior */}
        {mode === 'nueva' ? (
          <div className="mb-4">
            <p className="text-xs font-mono mb-1.5" style={{ color: cfg.accent }}>PLANOS DEL PROYECTO ✱ <span className="text-white/20">(PDFs e imágenes — todas las páginas)</span></p>
            <div
              onClick={() => multiInputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); }}
              onDrop={async e => { e.preventDefault(); if (e.dataTransfer.files?.length) await handleMultiFiles(e.dataTransfer.files); }}
              className={`flex flex-col items-center justify-center gap-3 min-h-36 p-4 rounded-xl border-2 border-dashed cursor-pointer transition-all duration-150 ${projectFiles.length > 0 ? 'border-green-500/30 bg-green-500/5' : 'border-white/10 bg-white/3 hover:border-white/20 hover:bg-white/5'}`}
            >
              <input ref={multiInputRef} type="file" accept="image/*,.pdf,application/pdf" multiple className="hidden" onChange={async e => { if (e.target.files?.length) await handleMultiFiles(e.target.files); e.target.value = ''; }} />
              {loadingFiles ? (
                <><Loader2 size={24} className="animate-spin text-white/40" /><span className="text-xs text-white/40">Procesando archivos…</span></>
              ) : projectFiles.length > 0 ? (
                <>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {projectFiles.map((pf, i) => (
                      <div key={i} className="relative group">
                        <img src={pf.url} alt={`Plano ${i+1}`} className="h-20 rounded-lg border border-white/10 object-contain" />
                        <button onClick={e => { e.stopPropagation(); setProjectFiles(prev => prev.filter((_, j) => j !== i)); }} className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X size={10} /></button>
                        <span className="absolute bottom-0.5 left-0.5 text-[9px] font-mono bg-black/60 text-white/60 px-1 rounded">{i+1}</span>
                      </div>
                    ))}
                  </div>
                  <span className="text-xs text-green-400/60">{projectFiles.length} página{projectFiles.length > 1 ? 's' : ''} cargada{projectFiles.length > 1 ? 's' : ''} — clic para añadir más</span>
                </>
              ) : (
                <><Upload size={24} className="text-white/30" /><div className="text-sm font-medium text-white/50">Arrastra aquí todos los planos del proyecto</div><div className="text-xs text-white/25 text-center">PDFs con múltiples páginas, imágenes de planos, etc.</div></>
              )}
            </div>
            {projectFiles.length > 0 && (
              <button onClick={() => setProjectFiles([])} className="mt-2 text-xs text-red-400/50 hover:text-red-400 transition-colors">Borrar todos los planos</button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div><p className="text-xs font-mono text-white/30 mb-1.5">ESTADO ACTUAL <span className="text-white/20">(opcional)</span></p><DropZone label="Plano actual" sub="Antes / solar / existente" img={imgA} onChange={async i => setImgA(i)} /></div>
            <div><p className="text-xs font-mono mb-1.5" style={{ color: cfg.accent }}>PLANO REFORMA ✱</p><DropZone label="Plano proyecto" sub="Resultado final" img={imgR} onChange={async i => setImgR(i)} /></div>
          </div>
        )}

        {/* Marker info */}
        {(imgA || imgR || projectFiles.length > 0) && (
          <div className="mb-4 flex items-center gap-2 flex-wrap">
            <MapPin size={14} className={markers.scale ? 'text-green-400' : 'text-white/30'} />
            <span className="text-xs text-white/40">{markers.scale ? '📏 Escala calibrada — mediciones exactas' : 'Sin escala — la IA estimará medidas'}</span>
          </div>
        )}

        {/* Params */}
        <div className="p-4 rounded-xl bg-white/5 border border-white/10 mb-4 space-y-3">
          <p className="text-xs font-mono text-white/30 mb-2">PARÁMETROS</p>
          <div className="grid grid-cols-2 gap-3">
            {mode === 'nueva' && (
              <div className="col-span-2">
                <label className="text-xs text-white/30 font-mono mb-1 block">TIPO DE OBRA</label>
                <select value={params.tipoObra} onChange={e => setParams(p => ({ ...p, tipoObra: e.target.value }))} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-white/30">
                  {TIPO_OBRA.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            )}
            {mode === 'exterior' && (
              <div className="col-span-2">
                <label className="text-xs text-white/30 font-mono mb-1 block">TIPO PROYECTO</label>
                <select value={params.tipoExterior} onChange={e => setParams(p => ({ ...p, tipoExterior: e.target.value }))} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-white/30">
                  {TIPO_EXTERIOR.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="text-xs text-white/30 font-mono mb-1 block">ALTURA LIBRE (m)</label>
              <input type="number" step="0.05" value={params.alturaLibre} onChange={e => setParams(p => ({ ...p, alturaLibre: Math.max(0.1, parseFloat(e.target.value) || 2.5) }))} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-white/30" />
            </div>
            <div>
              <label className="text-xs text-white/30 font-mono mb-1 block">HVAC</label>
              <select value={params.hvac} onChange={e => setParams(p => ({ ...p, hvac: e.target.value }))} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-white/30">
                {HVAC_OPTS.map(h => <option key={h.id} value={h.id}>{h.label}</option>)}
              </select>
            </div>
          </div>
          {mode !== 'exterior' && (
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm text-white/50 cursor-pointer"><input type="checkbox" checked={params.puertas} onChange={e => setParams(p => ({ ...p, puertas: e.target.checked }))} className="accent-blue-500" />Cambiar puertas</label>
              <label className="flex items-center gap-2 text-sm text-white/50 cursor-pointer"><input type="checkbox" checked={params.ventanas} onChange={e => setParams(p => ({ ...p, ventanas: e.target.checked }))} className="accent-blue-500" />Cambiar ventanas</label>
            </div>
          )}
          <textarea rows={2} placeholder="Notas para la IA…" value={params.notas} onChange={e => setParams(p => ({ ...p, notas: e.target.value }))} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/20 focus:outline-none focus:border-white/30 resize-none" />
        </div>

        <button onClick={scan} disabled={mode === 'nueva' ? projectFiles.length === 0 : (!imgA && !imgR)} className="w-full py-3 rounded-xl font-semibold text-sm text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed" style={{ background: (mode === 'nueva' ? projectFiles.length > 0 : (imgA || imgR)) ? `linear-gradient(135deg, ${cfg.accent}, ${cfg.accent}99)` : undefined, backgroundColor: (mode === 'nueva' ? projectFiles.length === 0 : (!imgA && !imgR)) ? '#1f2937' : undefined }}>
          <span className="flex items-center justify-center gap-2"><ScanLine size={16} />Analizar con IA →</span>
        </button>
      </div>
    </div>
  );

  // ── Quantities step ──────────────────────────────────────────────
  if (step === 'quantities') return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 p-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h2 className="text-lg font-bold text-white">Mediciones</h2>
            <p className="text-xs text-white/40">{scanResult?.resumen}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/60">{scanResult?.confianza === 'alta' ? '✅' : scanResult?.confianza === 'media' ? '⚠️' : '❌'} {scanResult?.confianza}</span>
            <span className="text-sm font-bold font-mono px-3 py-1 rounded-full border" style={{ color: cfg.accent, borderColor: `${cfg.accent}44`, backgroundColor: `${cfg.accent}11` }}>{eur(totalExVat)}</span>
          </div>
        </div>

        {/* Plan thumbnails */}
        {(imgA || imgR) && (
          <div className="flex gap-3 mb-4">
            {imgA && <div className="text-center cursor-pointer" onClick={() => window.open(imgA.url)}><p className="text-xs font-mono text-white/30 mb-1">ANTES</p><img src={imgA.url} alt="" className="h-16 rounded-lg border border-white/10 object-contain" /></div>}
            {imgR && <div className="text-center cursor-pointer" onClick={() => window.open(imgR.url)}><p className="text-xs font-mono text-white/30 mb-1">{imgA ? 'DESPUÉS' : 'PLANO'}</p><img src={imgR.url} alt="" className="h-16 rounded-lg border border-white/10 object-contain" /></div>}
            <p className="text-xs text-white/20 self-end pb-1">Clic para ampliar</p>
          </div>
        )}

        {/* Memoria Descriptiva */}
        {scanResult?.memoria && (
          <div className="mb-4 rounded-xl border border-purple-500/20 overflow-hidden">
            <button onClick={() => setOpenCats(o => ({ ...o, __memoria: !o.__memoria }))} className="w-full flex items-center justify-between px-4 py-2.5 bg-purple-500/5 hover:bg-purple-500/10 transition-colors">
              <div className="flex items-center gap-2">
                <span>📋</span>
                <span className="text-sm font-semibold text-purple-400">Memoria Descriptiva</span>
              </div>
              {openCats.__memoria ? <ChevronUp size={14} className="text-white/30" /> : <ChevronDown size={14} className="text-white/30" />}
            </button>
            {openCats.__memoria && (
              <div className="px-4 py-3 text-sm text-white/70 leading-relaxed whitespace-pre-wrap bg-purple-500/5">
                {scanResult.memoria}
              </div>
            )}
          </div>
        )}

        {/* Category tables */}
        <div className="space-y-2 mb-6">
          {cats.map(cat => {
            const nActive = cat.items.filter(it => (qty[it.id] ?? 0) > 0).length;
            const isOpen = openCats[cat.id] !== false;
            return (
              <div key={cat.id} className="rounded-xl overflow-hidden border" style={{ borderColor: nActive > 0 ? `${cat.color}44` : 'rgba(255,255,255,0.06)' }}>
                <button onClick={() => setOpenCats(o => ({ ...o, [cat.id]: !o[cat.id] }))} className="w-full flex items-center justify-between px-4 py-2.5 bg-white/5 hover:bg-white/8 transition-colors">
                  <div className="flex items-center gap-2">
                    <span>{cat.icon}</span>
                    <span className="text-sm font-semibold" style={{ color: nActive > 0 ? cat.color : 'rgba(255,255,255,0.5)' }}>{cat.label}</span>
                    {nActive > 0 && <span className="text-xs px-2 py-0.5 rounded-full font-mono" style={{ color: cat.color, backgroundColor: `${cat.color}1A` }}>{nActive}</span>}
                  </div>
                  {isOpen ? <ChevronUp size={14} className="text-white/30" /> : <ChevronDown size={14} className="text-white/30" />}
                </button>
                {isOpen && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="border-t border-white/5"><th className="px-4 py-2 text-left font-mono text-white/20">CÓD</th><th className="px-3 py-2 text-left text-white/20">DESCRIPCIÓN</th><th className="px-4 py-2 text-right text-white/20">CANTIDAD</th></tr></thead>
                      <tbody>
                        {cat.items.map(it => {
                          const q = qty[it.id] ?? 0;
                          return (
                            <tr key={it.id} className="border-t border-white/5 hover:bg-white/3 transition-colors" style={{ backgroundColor: q > 0 ? `${cat.color}08` : undefined }}>
                              <td className="px-4 py-2 font-mono text-white/30">{it.cod}</td>
                              <td className="px-3 py-2 text-white/60" style={{ color: q > 0 ? 'rgba(255,255,255,0.85)' : undefined }}>
                                <div className="flex items-center gap-1.5">
                                  <span>{it.label}</span>
                                  {customPrices[it.id]?.source === 'ddc' && (
                                    <span title="Precio DDC CWICR (55.000+ partidas)" className="text-[10px] bg-blue-500/20 text-blue-300 px-1.5 py-0.5 rounded font-mono">DDC</span>
                                  )}
                                  {q > 0 && hasGroqKey && (
                                    <span className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <button title="Explicar partida" onClick={async (e) => { e.stopPropagation(); setAiPanel({ type: 'explain', content: '', loading: true, itemName: it.label }); try { const p = customPrices[it.id]?.price ?? it.p; const txt = await explainBudgetItem(it.label, it.ud, p); setAiPanel({ type: 'explain', content: txt, loading: false, itemName: it.label }); } catch (err) { setAiPanel({ type: 'explain', content: `Error: ${err instanceof Error ? err.message : String(err)}`, loading: false, itemName: it.label }); } }} className="w-5 h-5 rounded bg-purple-500/20 text-purple-300 flex items-center justify-center text-[10px] hover:bg-purple-500/40 transition-colors">💡</button>
                                      <button title="Value Engineering (Alternativas)" onClick={async (e) => { e.stopPropagation(); setAiPanel({ type: 'alternatives', content: '', loading: true, itemName: it.label }); try { const p = customPrices[it.id]?.price ?? it.p; const txt = await suggestAlternatives(it.label, p, it.ud); setAiPanel({ type: 'alternatives', content: txt, loading: false, itemName: it.label }); } catch (err) { setAiPanel({ type: 'alternatives', content: `Error: ${err instanceof Error ? err.message : String(err)}`, loading: false, itemName: it.label }); } }} className="w-5 h-5 rounded bg-green-500/20 text-green-300 flex items-center justify-center text-[10px] hover:bg-green-500/40 transition-colors">💰</button>
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-2 text-right">
                                <input type="number" min={0} step={0.5} value={q || ''} placeholder="—"
                                  onChange={e => setQty(prev => ({ ...prev, [it.id]: Math.max(0, parseFloat(e.target.value) || 0) }))}
                                  className="w-20 bg-gray-900 text-right rounded-lg px-2 py-1 font-mono text-sm focus:outline-none focus:ring-1 transition-colors"
                                  style={{ border: `1px solid ${q > 0 ? cat.color + '66' : 'rgba(255,255,255,0.1)'}`, color: q > 0 ? cat.color : 'rgba(255,255,255,0.3)' }}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* AI Assistant Panel */}
        {aiPanel && (
          <div className="mb-4 rounded-xl border border-purple-500/30 bg-purple-500/5 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 bg-purple-500/10">
              <div className="flex items-center gap-2">
                <Zap size={14} className="text-purple-400" />
                <span className="text-sm font-semibold text-purple-300">
                  {aiPanel.type === 'explain' ? `Explicación: ${aiPanel.itemName}` : aiPanel.type === 'alternatives' ? `Value Engineering: ${aiPanel.itemName}` : '📊 Análisis de ahorro'}
                </span>
              </div>
              <button onClick={() => setAiPanel(null)} className="text-white/30 hover:text-white/60"><X size={14} /></button>
            </div>
            <div className="px-4 py-3">
              {aiPanel.loading ? (
                <div className="flex items-center gap-2 text-sm text-white/40"><Loader2 size={14} className="animate-spin" /> Pensando con Llama 3.3 70B…</div>
              ) : (
                <div className="text-sm text-white/70 leading-relaxed whitespace-pre-wrap">{aiPanel.content}</div>
              )}
            </div>
          </div>
        )}

        <div className="flex gap-3 justify-center flex-wrap">
          <button onClick={() => { setStep('upload'); }} className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-white/10 text-white/50 text-sm hover:text-white/80 hover:border-white/20 transition-colors"><RotateCcw size={14} />Re-analizar</button>
          {hasGroqKey && (
            <button onClick={async () => {
              const items = buildQuoteItems(mode, qty).filter(i => i.quantity > 0);
              if (items.length === 0) return;
              setAiPanel({ type: 'savings', content: '', loading: true });
              try {
                const analysis = await analyzeBudgetSavings(
                  items.map(i => ({ name: i.name, quantity: i.quantity, unit: i.unit, price: i.price, total: i.total })),
                  totalExVat
                );
                setAiPanel({ type: 'savings', content: analysis, loading: false });
              } catch (e) { setAiPanel({ type: 'savings', content: `Error: ${e instanceof Error ? e.message : String(e)}`, loading: false }); }
            }} className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-purple-500/30 text-purple-300 text-sm hover:bg-purple-500/10 transition-colors">
              <Zap size={14} />Analizar ahorro con IA
            </button>
          )}
          <button onClick={() => setStep('summary')} className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm text-white transition-all" style={{ background: `linear-gradient(135deg, ${cfg.accent}, ${cfg.accent}99)` }}>Ver resumen →</button>
        </div>
      </div>
    </div>
  );

  const pendingQuoteObj: BuildProQuote = scanResult ? {
    projectId, mode, tipoObra: params.tipoObra,
    resumen: scanResult.resumen, memoria: scanResult.memoria, superficie: scanResult.superficie, confianza: scanResult.confianza,
    items: buildQuoteItems(mode, qty, customPrices).map(({ id, name, quantity, unit, price, total }) => ({ id, name, quantity, unit, price, total })),
    totalExVat, vat21: totalExVat * VAT, totalIncVat: totalExVat * (1 + VAT), createdAt: new Date().toISOString(), plantas: parseInt(scanResult.plantas || '1') || 1
  } : {} as any;

  // ── Summary step ─────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 p-6 print:bg-white print:p-0 relative">
      
      {showCad && <CadGenerator onClose={() => setShowCad(false)} contextPrompt={scanResult?.resumen || ''} />}
      
      {/* Hidden printable PDF layout */}
      {scanResult && <QuotePDFRenderer quote={pendingQuoteObj} params={params} id={savedQuoteId || `PRE-${Date.now().toString().slice(-6)}`} />}

      {/* Interactive UI (hide when printing) */}
      <div className="max-w-3xl mx-auto print:hidden">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h2 className="text-lg font-bold text-white">Resumen de presupuesto</h2>
            <p className="text-xs text-white/40">{cfg.icon && <cfg.icon size={11} className="inline mr-1" />}{cfg.label}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowPrices(s => !s)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 text-white/50 text-xs hover:text-white/80 transition-colors">{showPrices ? <EyeOff size={13} /> : <Eye size={13} />}{showPrices ? 'Ocultar precios' : 'Ver precios'}</button>
            <button onClick={() => window.print()} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 text-white/80 text-xs hover:bg-white/10 transition-colors"><Download size={13} />PDF</button>
            {savedQuoteId ? (
              <span className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-green-400 bg-green-500/10 text-xs font-semibold border border-green-500/20"><CheckCircle2 size={13} />Guardado</span>
            ) : (
              <button onClick={saveQuote} disabled={saving} className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-white text-xs font-semibold transition-all disabled:opacity-50" style={{ background: `linear-gradient(135deg, ${cfg.accent}, ${cfg.accent}99)` }}>{saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}{saving ? 'Guardando…' : 'Guardar presupuesto'}</button>
            )}
          </div>
        </div>

        {/* Memoria Descriptiva in summary */}
        {scanResult?.memoria && (
          <div className="mb-4 rounded-xl border border-purple-500/20 overflow-hidden">
            <div className="px-4 py-2.5 bg-purple-500/5 flex items-center gap-2">
              <span>📋</span>
              <span className="text-sm font-semibold text-purple-400">Memoria Descriptiva</span>
            </div>
            <div className="px-4 py-3 text-sm text-white/70 leading-relaxed whitespace-pre-wrap bg-purple-500/5">
              {scanResult.memoria}
            </div>
          </div>
        )}

        <div className="p-3 rounded-xl bg-yellow-500/5 border border-yellow-500/20 text-yellow-400/80 text-xs mb-4 flex items-start gap-2">
          <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
          Precios orientativos 2024 (España). Solicita presupuesto real a contratistas.
        </div>

        {/* Category totals */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
          {cats.map(cat => {
            const v = cat.items.reduce((s, it) => s + it.p * (qty[it.id] ?? 0), 0);
            if (!v) return null;
            return <div key={cat.id} className="p-3 rounded-xl bg-white/5 border" style={{ borderColor: `${cat.color}22` }}><p className="text-xs font-mono text-white/30 mb-1">{cat.icon} {cat.label.toUpperCase()}</p><p className="text-base font-bold font-mono" style={{ color: cat.color }}>{eur(v)}</p></div>;
          })}
        </div>

        {/* Line items */}
        <div className="space-y-2 mb-6">
          {cats.map(cat => {
            const active = cat.items.filter(it => (qty[it.id] ?? 0) > 0);
            if (!active.length) return null;
            return (
              <div key={cat.id} className="rounded-xl overflow-hidden border border-white/6">
                <div className="px-4 py-2 flex justify-between items-center" style={{ backgroundColor: `${cat.color}0C` }}>
                  <span className="text-sm font-semibold" style={{ color: cat.color }}>{cat.icon} {cat.label}</span>
                  {showPrices && <span className="font-mono text-sm font-bold" style={{ color: cat.color }}>{eur(active.reduce((s, it) => s + it.p * (qty[it.id] ?? 0), 0))}</span>}
                </div>
                <table className="w-full text-xs">
                  <tbody>
                    {active.map((it, i) => {
                      const q = qty[it.id] ?? 0;
                      return (
                        <tr key={it.id} className={i > 0 ? 'border-t border-white/5' : ''}>
                          <td className="px-4 py-2 font-mono text-white/25 w-20">{it.cod}</td>
                          <td className="px-3 py-2 text-white/70">{it.label}</td>
                          <td className="px-3 py-2 font-mono font-semibold text-right w-16" style={{ color: cat.color }}>{q % 1 === 0 ? q : q.toFixed(1)}</td>
                          <td className="px-2 py-2 font-mono text-white/30 w-10">{it.ud}</td>
                          {showPrices && <><td className="px-2 py-2 font-mono text-white/25 w-16 text-right">{it.p}€</td><td className="px-4 py-2 font-mono text-white/50 text-right w-20">{eur(it.p * q)}</td></>}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>

        {/* DDC Professional Cost Summary */}
        {(() => {
          const activeItems = buildQuoteItems(mode, qty, customPrices).filter(i => i.quantity > 0);
          const projSummary = calculateProjectSummary(
            activeItems.map(i => ({ name: i.name, quantity: i.quantity, unit: i.unit, price: i.price, total: i.total }))
          );
          const wasteData = calculateProjectWaste(
            activeItems.map(i => ({ name: i.name, quantity: i.quantity, price: i.price }))
          );
          const veTargets = identifyVETargets(
            activeItems.map(i => ({ name: i.name, quantity: i.quantity, price: i.price, total: i.total })),
            5, 3.0
          );

          return (
            <>
              {/* Professional cost breakdown */}
              <div className="rounded-2xl border overflow-hidden mb-4" style={{ borderColor: `${cfg.accent}44` }}>
                <div className="p-4" style={{ background: `linear-gradient(135deg, ${cfg.accent}11, #130D2222)` }}>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-semibold text-white/50 tracking-wider">RESUMEN DE PRESUPUESTO (DDC)</p>
                    <p className="text-2xl font-bold font-mono" style={{ color: cfg.accent }}>{eur(projSummary.totalConIva)}</p>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    <div className="p-2.5 rounded-lg bg-white/5">
                      <p className="text-white/30 mb-0.5">PEM</p>
                      <p className="font-mono font-semibold text-white/80">{eur(projSummary.pem)}</p>
                    </div>
                    <div className="p-2.5 rounded-lg bg-white/5">
                      <p className="text-white/30 mb-0.5">GG 13%</p>
                      <p className="font-mono font-semibold text-orange-300/80">{eur(projSummary.gg)}</p>
                    </div>
                    <div className="p-2.5 rounded-lg bg-white/5">
                      <p className="text-white/30 mb-0.5">BI 6%</p>
                      <p className="font-mono font-semibold text-blue-300/80">{eur(projSummary.bi)}</p>
                    </div>
                    <div className="p-2.5 rounded-lg bg-white/5">
                      <p className="text-white/30 mb-0.5">IVA 21%</p>
                      <p className="font-mono font-semibold text-green-300/80">{eur(projSummary.iva)}</p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
                    <div className="flex gap-4 text-xs text-white/30">
                      <span>PEC: <span className="font-mono text-white/50">{eur(projSummary.totalPEC)}</span></span>
                      <span>MO ~{projSummary.laborPct}%</span>
                      <span>MAT ~{projSummary.materialPct}%</span>
                    </div>
                    <p className="text-xs text-white/20">DDC CWICR · 55.719 partidas</p>
                  </div>
                </div>
              </div>

              {/* Waste / Mermas Analysis Panel */}
              {wasteData.totalWasteCost > 0 && (
                <div className="rounded-xl border border-amber-500/20 overflow-hidden mb-4">
                  <button
                    onClick={() => setOpenCats(o => ({ ...o, __waste: !o.__waste }))}
                    className="w-full flex items-center justify-between px-4 py-2.5 bg-amber-500/5 hover:bg-amber-500/10 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span>📦</span>
                      <span className="text-sm font-semibold text-amber-400">Mermas Estimadas</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 font-mono">{wasteData.avgWastePct}%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-amber-300/60">{eur(wasteData.totalWasteCost)}</span>
                      {openCats.__waste ? <ChevronUp size={14} className="text-white/30" /> : <ChevronDown size={14} className="text-white/30" />}
                    </div>
                  </button>
                  {openCats.__waste && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-t border-amber-500/10">
                            <th className="px-4 py-2 text-left text-white/20">MATERIAL</th>
                            <th className="px-3 py-2 text-right text-white/20">TIPO</th>
                            <th className="px-3 py-2 text-right text-white/20">MERMA</th>
                            <th className="px-3 py-2 text-right text-white/20">PEDIR</th>
                            <th className="px-4 py-2 text-right text-white/20">COSTE</th>
                          </tr>
                        </thead>
                        <tbody>
                          {wasteData.items.filter(w => w.wasteCost > 0).map((w, i) => (
                            <tr key={i} className="border-t border-white/5">
                              <td className="px-4 py-1.5 text-white/60">{activeItems[i]?.name || '—'}</td>
                              <td className="px-3 py-1.5 text-right font-mono text-amber-300/50">{w.materialType}</td>
                              <td className="px-3 py-1.5 text-right font-mono text-amber-300">{w.totalWastePct}%</td>
                              <td className="px-3 py-1.5 text-right font-mono text-white/40">{w.orderQuantity}</td>
                              <td className="px-4 py-1.5 text-right font-mono text-amber-300/80">{eur(w.wasteCost)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* VE Optimization Panel */}
              {veTargets.length > 0 && (
                <div className="rounded-xl border border-emerald-500/20 overflow-hidden mb-4">
                  <button
                    onClick={() => setOpenCats(o => ({ ...o, __ve: !o.__ve }))}
                    className="w-full flex items-center justify-between px-4 py-2.5 bg-emerald-500/5 hover:bg-emerald-500/10 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span>💡</span>
                      <span className="text-sm font-semibold text-emerald-400">Optimización (Ingeniería de Valor)</span>
                    </div>
                    {openCats.__ve ? <ChevronUp size={14} className="text-white/30" /> : <ChevronDown size={14} className="text-white/30" />}
                  </button>
                  {openCats.__ve && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-t border-emerald-500/10">
                            <th className="px-4 py-2 text-left text-white/20">PARTIDA</th>
                            <th className="px-3 py-2 text-right text-white/20">COSTE</th>
                            <th className="px-3 py-2 text-right text-white/20">% PPTO</th>
                            <th className="px-4 py-2 text-left text-white/20">OPORTUNIDAD</th>
                          </tr>
                        </thead>
                        <tbody>
                          {veTargets.map((t, i) => (
                            <tr key={i} className="border-t border-white/5">
                              <td className="px-4 py-1.5 text-white/60">{t.name}</td>
                              <td className="px-3 py-1.5 text-right font-mono text-white/50">{eur(t.total)}</td>
                              <td className="px-3 py-1.5 text-right font-mono" style={{ color: t.pct > 10 ? '#f87171' : t.pct > 5 ? '#fbbf24' : '#4ade80' }}>{t.pct}%</td>
                              <td className="px-4 py-1.5 text-white/50">{t.veOpportunity}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="px-4 py-2 text-xs text-emerald-300/40 border-t border-white/5">
                        💡 Partidas con más del 10% del presupuesto son candidatas prioritarias para ingeniería de valor
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          );
        })()}

        <div className="flex gap-3 justify-center mt-5 flex-wrap">
          <button onClick={() => setShowCad(true)} className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-emerald-500/50 bg-emerald-500/10 text-emerald-400 font-semibold text-sm hover:bg-emerald-500/20 transition-all shadow-[0_0_15px_rgba(16,185,129,0.15)]"><Cuboid size={16} />Generar Modelo 3D (CAD)</button>
          <button onClick={() => setStep('quantities')} className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-white/10 text-white/50 text-sm hover:text-white/80 transition-colors">← Editar mediciones</button>
          <button onClick={goHome} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm transition-colors" style={{ border: `1px solid ${cfg.accent}44`, color: cfg.accent }}>
            <Home size={14} />Nuevo proyecto
          </button>
        </div>
      </div>
    </div>
  );
};

// ── VerticalDescriptor — consumed by VerticalEngine ───────────────
export const buildProDescriptor: VerticalDescriptor = {
  id:        'buildpro',
  name:      'BuildPro',
  version:   '1.0.0',
  component: BuildProComponent,
};

export default BuildProComponent;
